import React, { Fragment, useRef, useEffect, useState } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import { useParams, useLocation } from 'react-router-dom';
import ReactInfiniteScroller from 'react-infinite-scroller';

import ActionBar, { FilterOperatorInputText, FilterOperatorInputPercent } from '../../ActionBar';
import { SortDirection } from '../../../types/sort';
import { ResourceStatus } from '../../../types/resource';
import { ALL_ORGANIZATIONS, deserializeOrganization } from '../../../types/all-organizations';
import DataGrid from '../../DataGrid';
import ErrorState from '../../ErrorState';
import Loading from '../../Loading';

import MultiPointInstallationListStore from '../../../rx-stores/multi-point-installation-list';
import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import singleClickDetector from '../../../utils/single-click-detector';
import { openInNewTab } from '../../../utils/navigation';
import {
  MultiPointInstallation,
  MultiPointInstallationSensor,
} from '../../../types/api';
import { FilterOperator } from '../../../types/filters';

import { MultiPointInstallationListActionTypes } from '../../../types/multi-point-installation-list';

import { deserializeListViewState } from '../../../utils/rx-store-serialization';
import { ON_PREM } from '../../../utils/environment';

import {
  AppBar,
  AppBarTitle,
  AppBarSection,
  Button,
  Checkbox,
  Dialogger,
  Icons,
} from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import MoreButton from '../../MoreButton';
import Popup from '../../Popup';
import HorizontalForm from '../../HorizontalForm';
import StatusTag from '../../StatusTag';
import Tooltip from '../../Tooltip';
import ListView, {
  ListViewColumn,
  ListViewColumnSpacer,
  getNextSortDirection,
} from '../../ListView';
import ListViewTwoRowHeader from '../../ListViewTwoRowHeader';

import CreationModal from '../Creation';
import CaptureScheduleModal from '../../CaptureScheduleModal';
import SensorActionButtons from '../../Sensors/ActionButtons';
import ColoredPercent from '../../ColoredPercent';
import ColoredQaedEventCount from '../../ColoredQaedEventCount';
import COLUMN_SETS from '../../InConfiguration/columnSets';

const MDASH = <Fragment>&mdash;</Fragment>;

// A component for rendering a horizontal list of items, all of the same height.
//
// Any that don't fit onto a single row are now shown, and a `+ n more` button is shown after the
// list that when clicked, opens a popup containing the rest of the items.
const OverflowingHorizontalList: React.FunctionComponent<{
  data: Array<any>;
  itemHeight: React.ReactText; // The height of each item
  renderItem: (item: any) => React.ReactNode;
}> = ({ data, renderItem, itemHeight }) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [initialItemWrapIndex, setInitialItemWrapIndex] = useState<number>(-1);

  function recalculate() {
    if (!wrapper.current) {
      return;
    }

    const wrapperTop = wrapper.current.getBoundingClientRect().top;

    const initialItemWrapIndex = Array.from(wrapper.current.children).findIndex(
      item => {
        const { top } = item.getBoundingClientRect();
        return top > wrapperTop;
      },
    );
    setInitialItemWrapIndex(initialItemWrapIndex);

    const wrappedData =
      initialItemWrapIndex === -1 || initialItemWrapIndex === data.length
        ? []
        : data.slice(initialItemWrapIndex);
  }

  // Recalculate the items that are on screen vs the items that have overflowed / should be in the
  // popup when:
  //
  // 1. Item becomes visible on screen via `IntersectionObserver` API (this should be fast / optimized)
  //    NOTE: this is done based on the body element, in the future it this is made more gneric,
  //    this "parent" element probably needs to be customizable.
  //
  // 2. Window resizes, possibly causing page to reflow / etc
  //
  useEffect(() => {
    recalculate();
    const observer = new IntersectionObserver(
      entries => {
        const isInViewport = entries[0].isIntersecting;
        if (isInViewport) {
          recalculate();
          window.addEventListener('resize', recalculate);
        } else {
          window.removeEventListener('resize', recalculate);
        }
      },
      { threshold: [0] },
    );

    observer.observe(document.body);
    return () => observer.unobserve(document.body);
  }, []);

  const renderedData = data.map(item => (
    <div key={item.id}>{renderItem(item)}</div>
  ));

  // Determine what items "wrapped" to the next line and should be shown in the popup
  const wrappedData =
    initialItemWrapIndex === -1 || initialItemWrapIndex === 0
      ? []
      : renderedData.slice(initialItemWrapIndex);

  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  return (
    <div className={styles.overflowingHorizontalListWrapper}>
      <div
        className={styles.overflowingHorizontalListInner}
        ref={wrapper}
        style={{ height: itemHeight }}
      >
        {renderedData}
      </div>
      {wrappedData.length > 0 ? (
        <Popup
          open={popupOpen}
          onOpen={() => setPopupOpen(true)}
          onClose={() => setPopupOpen(false)}
          target={
            <Button variant="underline">+ {wrappedData.length} more</Button>
          }
          position={{ right: 0, top: 44 }}
        >
          {wrappedData}
        </Popup>
      ) : null}
    </div>
  );
};

const MultiPointInstallationList: React.FunctionComponent<{}> = () => {
  const state = useRxStore(MultiPointInstallationListStore);
  const dispatch = useRxDispatch();
  const location = useLocation();
  const { organizationIdOrAll } = useParams();

  let scrollParentRef = useRef<HTMLDivElement>(null);

  // The multipoint installation opened in the sidebar, or `null` if nothing is opened.
  const openedMultiPointInstallation: MultiPointInstallation | null =
    state.resource.status === ResourceStatus.COMPLETE &&
      state.sidePaneMultiPointInstallationId
      ? state.resource.data.items.find(
        i => i.id === state.sidePaneMultiPointInstallationId,
      ) || null
      : null;

  const [mpiCreationModalOpen, setMPICreationModalOpen] = useState(false);

  const [captureScheduleModalOpen, setCaptureScheduleModalOpen] = useState(
    false,
  );

  useEffect(() => {
    const { filters, searchText, sort } = deserializeListViewState(
      location.search.replace(/^\?/, ''),
    );

    dispatch({
      type:
        MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_PAGE,
      searchText,
      filters,
      sort,
      organizationId: deserializeOrganization(organizationIdOrAll),
    });
  }, [organizationIdOrAll]);

  const visibleData =
    state.resource.status === ResourceStatus.COMPLETE
      ? state.resource.data.items
      : [];

  // The value of the `sensors` prop passed to the Capture Schedule Modal
  // indicates which sensors need capture schedules.
  const selectedSensors = visibleData
    .filter(mpi => state.selectedMultiPointInstallationIds.includes(mpi.id))
    .flatMap(mpi =>
      mpi.sensors.map(sensor => ({
        mpi,
        sensor,
      })),
    )
    .reduce((acc, { sensor, mpi }) => {
      // Deduplicate the merged sensors list
      if (!acc.find(i => i.sensor.serial_number === sensor.serial_number)) {
        return [...acc, { sensor, mpi }];
      } else {
        return acc;
      }
    }, [] as Array<{ mpi: MultiPointInstallation; sensor: MultiPointInstallationSensor }>)
    .map(({ mpi, sensor }) => ({
      serial_number: sensor.serial_number,
      link_id: sensor.link_id,
      organization: {
        id: mpi.organization_id,
        name: mpi.organization_name,
      },
      sensor_type: sensor.sensor_type,
      os_version: sensor.os_version,
      doorway_name: sensor.doorway_name,
      diagnostic_info_json: sensor.diagnostic_info_json,
      in_mpi: true,
    }));

  const header = (
    <Fragment>
      <AppBar>
        <AppBarTitle>Multi Point Installations</AppBarTitle>
        <AppBarSection>
          <Button
            variant="filled"
            onClick={() => setMPICreationModalOpen(true)}
            data-test-id="mpi-list-add-button"
            disabled={organizationIdOrAll === ALL_ORGANIZATIONS}
          >
            Add Multi Point Installation
          </Button>
        </AppBarSection>
      </AppBar>
      <ActionBar
        units="MPIs"
        totalItemCount={
          state.resource.status === ResourceStatus.COMPLETE
            ? state.resource.data.total
            : null
        }
        selectedItemCount={state.selectedMultiPointInstallationIds.length}
        onClearSelectedItems={() =>
          dispatch({
            type:
              MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_CHECKS,
            ids: state.selectedMultiPointInstallationIds,
            checked: false,
          })
        }
        selectedActionButtons={
          <SensorActionButtons
            presentation="list"
            sensors={selectedSensors}
          />
        }
        showSearch
        searchPlaceholder="Search by name, serial number, or doorway"
        searchWidth={400}
        searchValue={state.searchText}
        onChangeSearch={e =>
          dispatch({
            type:
              MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SEARCH_TEXT,
            organizationId: deserializeOrganization(organizationIdOrAll),
            text: e.target.value,
          })
        }
        filterDefinitions={[
          {
            id: 'number_of_sensors',
            label: 'Number of Sensors',
            operators: [
              {
                id: 'gt',
                label: 'is greater than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'gte',
                label: 'is greater than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lt',
                label: 'is less than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lte',
                label: 'is less than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
            ],
          },
          {
            id: 'fully_annotated_capture_group_count',
            label: 'Fully Ann. Cap. Groups',
            operators: [
              {
                id: 'gt',
                label: 'is greater than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'gte',
                label: 'is greater than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lt',
                label: 'is less than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lte',
                label: 'is less than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
            ],
          },
          {
            id: 'total_accuracy',
            label: 'Total Accuracy',
            operators: [
              {
                id: 'gt',
                label: 'is greater than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputPercent,
              } as FilterOperator<string>,
              {
                id: 'gte',
                label: 'is greater than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputPercent,
              } as FilterOperator<string>,
              {
                id: 'lt',
                label: 'is less than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputPercent,
              } as FilterOperator<string>,
              {
                id: 'lte',
                label: 'is less than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputPercent,
              } as FilterOperator<string>,
            ],
          },
        ]}
        filters={state.filters}
        onChangeFilters={filters => {
          dispatch({
            type: MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_FILTERS,
            organizationId: deserializeOrganization(organizationIdOrAll),
            filters,
          })
        }}
      />
    </Fragment>
  );

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <Fragment>
          {header}
          <Loading />
        </Fragment>
      );
    case ResourceStatus.ERROR:
      return <ErrorState error={state.resource.error} />;
    case ResourceStatus.COMPLETE:
      const columnSet = COLUMN_SETS.sidebar({
        onPrem: ON_PREM,
      });

      const allVisibleDataChecked =
        visibleData.length > 0 &&
        visibleData.every(item =>
          state.selectedMultiPointInstallationIds.includes(item.id),
        );

      return (
        <Fragment>
          {header}

          <CreationModal
            open={mpiCreationModalOpen}
            onDismiss={() => setMPICreationModalOpen(false)}
            sensors={[]}
          />

          <div
            className={styles.mpisList}
            ref={scrollParentRef}
            data-test-id="mpi-list-wrapper"
          >
          <ReactInfiniteScroller
            pageStart={1}
            loadMore={page =>
              dispatch({
                type: MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_PAGE,
                value: page,
              })
            }
            hasMore={
              state.resource.data.total >
              state.resource.data.items.length &&
              state.resource.data.fetchingMoreMPIs.status ===
              ResourceStatus.IDLE
            }
            useWindow={false}
            getScrollParent={() => scrollParentRef.current}
          >
            <ListView
              showHeaders={true}
              padOuterColumns={true}
              data={visibleData}
              sort={[state.sort]}
              headerHeight={48}
              isRowSelected={item => state.selectedMultiPointInstallationIds.includes(item.id)}
              onClickHeader={(column: string) => {
                // Don't allow fitlering these columns
                if (
                  column === 'Checkboxes' ||
                  column === 'Details' ||
                  column === 'sensors'
                ) {
                  return;
                }

                const lastSortDirection =
                  column === state.sort.column ? state.sort.direction : 'none';
                const sortDirection: SortDirection = getNextSortDirection(
                  lastSortDirection,
                );

                dispatch({
                  type:
                    MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SORT,
                  column,
                  direction: sortDirection,
                  organizationId: deserializeOrganization(organizationIdOrAll),
                });
              }}
              onMouseDownRow={item => {
                return singleClickDetector(() => {
                  let url: string;
                  if (state.resource.status === ResourceStatus.COMPLETE) {
                    url = state.resource.data.m2m_capture_group_enabled
                      ? `#/${organizationIdOrAll}/multi-point-installations/${item.id}/m2m-captures`
                      : `#/${organizationIdOrAll}/multi-point-installations/${item.id}/captures`
                  }
                  openInNewTab(url);
                });
              }}
            >
              <ListViewColumn
                id="Checkboxes"
                title={
                  <Checkbox
                    checked={allVisibleDataChecked}
                    data-test-id='list-row-checkbox'
                    onChange={e =>
                      dispatch({
                        type:
                          MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_CHECKS,
                        ids: visibleData.map(i => i.id),
                        checked: e.target.checked,
                      })
                    }
                    color={colorVariables.midnight}
                  />
                }
                width={60}
                template={(item: MultiPointInstallation) => (
                  <div onMouseDown={e => e.stopPropagation()}>
                    <Checkbox
                      checked={state.selectedMultiPointInstallationIds.includes(
                        item.id,
                      )}
                      onChange={e =>
                        dispatch({
                          type:
                            MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_CHECKS,
                          ids: [item.id],
                          checked: e.target.checked,
                        })
                      }
                      color={colorVariables.midnight}
                    />
                  </div>
                )}
              />
              <ListViewColumn
                id="name"
                title="Name"
                // Change this width of this column based on if the "organizations" column
                // is shown after it.
                minWidth={organizationIdOrAll === ALL_ORGANIZATIONS ? 120 : 280}
                width={organizationIdOrAll === ALL_ORGANIZATIONS ? 160 : 280}
                template={(item: MultiPointInstallation) => (
                  <div>
                    <Tooltip
                      target={
                        <div
                          className={styles.mpiName}
                          style={{
                            minWidth:
                              organizationIdOrAll === ALL_ORGANIZATIONS
                                ? 120
                                : 280,
                            width:
                              organizationIdOrAll === ALL_ORGANIZATIONS
                                ? 160
                                : 280,
                          }}
                        >
                          {item.name}
                        </div>
                      }
                      contents={item.name}
                    />
                    {item.common_spaces && item.common_spaces.length > 0 ? (
                      <Tooltip
                        target={
                          <div
                            className={styles.mpiSpaceName}
                            style={{
                              minWidth:
                                organizationIdOrAll === ALL_ORGANIZATIONS
                                  ? 120
                                  : 280,
                              width:
                                organizationIdOrAll === ALL_ORGANIZATIONS
                                  ? 160
                                  : 280,
                            }}
                          >
                            <div className={styles.mpiSpaceNameIcon}>
                              <Icons.Space
                                width={12}
                                height={12}
                                color="currentColor"
                              />
                            </div>
                            {item.common_spaces[0].name}
                          </div>
                        }
                        contents={
                          <Fragment>
                            <strong>Spaces:</strong>
                            <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 24 }}>
                              {item.common_spaces.map(i => (
                                <li key={i.id}>{i.name}</li>
                              ))}
                            </ul>
                          </Fragment>
                        }
                      />
                    ) : null}
                  </div>
                )}
              />
              {organizationIdOrAll === ALL_ORGANIZATIONS ? (
                <ListViewColumn
                  id="organization_name"
                  title="Organization"
                  width={120}
                  template={(item: MultiPointInstallation) => (
                    <Tooltip
                      target={
                        <div className={styles.organizationName}>
                          {item.organization_name}
                        </div>
                      }
                      contents={item.organization_name}
                    />
                  )}
                />
              ) : null}
              <ListViewColumn
                id="number_of_sensors"
                title="Number of Sensors"
                width={170}
                template={(item: MultiPointInstallation) => item.sensors.length}
              />
              <ListViewColumn
                id="fully_annotated_capture_group_count"
                title={
                  <Tooltip
                    target={
                      <ListViewTwoRowHeader
                        header="Fully Ann. Cap. Groups"
                        subheader={
                          <div
                            className={styles.annotatedCaptureGroupsLearnMore}
                          >
                            Learn more
                            <div className={styles.annotatedCaptureGroupsInfo}>
                              <Icons.Info
                                width={12}
                                height={12}
                                color="currentColor"
                              />
                            </div>
                          </div>
                        }
                      />
                    }
                    contents="The total number of capture groups that contain exclusively fully annotated captures."
                  />
                }
                width={180}
                template={(item: MultiPointInstallation) => (
                  <Fragment>
                    <span className={styles.annotatedCaptureGroupCount}>
                      {typeof item.fully_annotated_capture_group_count ===
                        'number'
                        ? item.fully_annotated_capture_group_count
                        : MDASH}
                    </span>
                    {typeof item.capture_group_count === 'number' ? (
                      <span className={styles.captureGroupCount}>
                        / {item.capture_group_count} total
                      </span>
                    ) : null}
                  </Fragment>
                )}
              />
              <ListViewColumn
                id="total_dedupe_accuracy"
                title={
                  <ListViewTwoRowHeader
                    header="Total Accuracy"
                    subheader="From the latest report"
                  />
                }
                width={150}
                template={(item: MultiPointInstallation) => (
                  <ColoredPercent percent={item.total_dedupe_accuracy} />
                )}
              />
              <ListViewColumn
                id="sensors"
                title="Sensors"
                width="auto"
                template={(item: MultiPointInstallation) => (
                  <div
                    className={styles.sensorList}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <OverflowingHorizontalList
                      data={item.sensors.sort(
                        (a, b) => a.position - b.position,
                      )}
                      itemHeight={50}
                      renderItem={sensor => (
                        <a
                          className={styles.sensorListItem}
                          href={`#/${organizationIdOrAll}/sensors/${sensor.serial_number}`}
                          key={sensor.sensor_serial}
                        >
                          <div className={styles.sensorListItemTopRow}>
                            <span className={styles.sensorListItemSerialNumber}>
                              {sensor.sensor_serial}
                            </span>
                            <span className={styles.sensorListItemStatus}>
                              <StatusTag
                                status={sensor.current_status}
                                size="small"
                              />
                            </span>
                          </div>
                          <div className={styles.sensorListItemQadEvents}>
                            QA'd events:{' '}
                            <ColoredQaedEventCount
                              isMPI={true}
                              value={sensor.qaed_event_count}
                            />
                          </div>
                        </a>
                      )}
                    />
                  </div>
                )}
              />
              <ListViewColumn
                id="Details"
                title=" "
                width={64}
                template={(item: MultiPointInstallation) => (
                  <HorizontalForm>
                    <div onMouseDown={e => e.stopPropagation()}>
                      <MoreButton
                        choices={[
                          {
                            id: 'edit',
                            label: 'Edit MPI',
                            href: `#/${organizationIdOrAll}/multi-point-installations/${item.id}/edit`,
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            onClick: () => {
                              Dialogger.confirm({
                                prompt: `Are you sure you would like to delete ${item.name}?`,
                                confirmText: 'Delete',
                              }).then(ok => {
                                if (!ok) {
                                  return;
                                }

                                dispatch({
                                  type:
                                    MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_DELETE,
                                  organizationId: deserializeOrganization(organizationIdOrAll),
                                  id: item.id,
                                });
                              });
                            },
                          },
                        ]}
                      />
                    </div>
                  </HorizontalForm>
                )}
              />
            </ListView>
            </ReactInfiniteScroller>
          </div>
          {state.resource.data.fetchingMoreMPIs.status ===
            ResourceStatus.LOADING ? (
            <div className={styles.infiniteScrollLoading}>
              Loading more MPIs...
            </div>
          ) : null}
          {state.resource.data.fetchingMoreMPIs.status ===
            ResourceStatus.ERROR ? (
            <div
              className={classnames(styles.infiniteScrollLoading, styles.error)}
            >
              Error loading mpis:{' '}
              {state.resource.data.fetchingMoreMPIs.error.toString()}
            </div>
          ) : null}
        </Fragment>
      );
  }
};

export default MultiPointInstallationList;
